'use client'
import React, { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import Footer from '@/Components/Footer'
import Header from '@/Components/HeaderComp'
import { Dialog, DialogBackdrop, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { XMarkIcon, FunnelIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

export default function Category({
  vendors = [],
  categories = [],
  sizes = [],
  colors = [],
  products = {},
  searchTerm: initialSearchTerm = '',
  selectedColors = [],
  selectedSizes = [],
  selectedVendors = [],
  selectedCategories = [],
  pageTitle = "",
  category = null,
  categoryDescription = "",
  productCount = 0,
  canonicalUrl = "",
  bannerMessage = "",
  perPage: initialPerPage = 12,
  navigation = [],
  footerNavigation = []
}) {
  const [filterColors, setFilterColors] = useState(selectedColors)
  const [filterSizes, setFilterSizes] = useState(selectedSizes)
  const [filterVendors, setFilterVendors] = useState(selectedVendors)
  const [filterCats, setFilterCats] = useState(selectedCategories)
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? '')
  const [perPage, setPerPage] = useState(initialPerPage)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  function toggle(value, list, setList) {
    if (list.includes(value)) {
      setList(list.filter((v) => v !== value))
    } else {
      setList([...list, value])
    }
  }

  function onColorChange(e) { toggle(e.target.value, filterColors, setFilterColors) }
  function onSizeChange(e) { toggle(e.target.value, filterSizes, setFilterSizes) }
  function onVendorChange(e) { toggle(e.target.value, filterVendors, setFilterVendors) }
  function onCategoryChange(e) { toggle(e.target.value, filterCats, setFilterCats) }

  function applyFilters(overridePerPage) {
    const pp = overridePerPage ?? perPage
    router.visit(route('public.category'), {
      method: 'get',
      data: { colors: filterColors, sizes: filterSizes, vendors: filterVendors, cats: filterCats, search: searchTerm, per_page: pp },
      preserveScroll: true,
      preserveState: true,
    })
  }

  function handleSearch(e) {
    e.preventDefault()
    router.visit(route('public.category'), {
      method: 'get',
      data: { search: searchTerm, per_page: perPage },
      preserveScroll: true,
      preserveState: true,
    })
  }

  function goToPage(url) {
    if (!url) return
    router.visit(url, {
      method: 'get',
      preserveScroll: true,
      preserveState: true,
    })
  }

  function changePerPage(value) {
    const pp = Number(value)
    setPerPage(pp)
    applyFilters(pp)
  }

  const {
    data: rawProductItems = [],
    current_page = 1,
    last_page = 1,
    prev_page_url = null,
    next_page_url = null,
  } = products ?? {}
  const productItems = Array.isArray(rawProductItems) ? rawProductItems : []

  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Search */}
      <form onSubmit={handleSearch}>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#F93A00] focus:ring-1 focus:ring-[#F93A00]"
          />
          <button
            type="submit"
            className="bg-[#F93A00] px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-[#d93200] transition-colors"
          >
            Go
          </button>
        </div>
      </form>

      {/* Colors */}
      {colors.length > 0 && (
        <Disclosure defaultOpen>
          {({ open }) => (
            <div className="border-t border-gray-200 pt-4">
              <DisclosureButton className="flex w-full items-center justify-between text-sm font-bold uppercase tracking-wide text-gray-800">
                Colors
                <ChevronDownIcon className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </DisclosureButton>
              <DisclosurePanel className="mt-4 space-y-2">
                {colors.map((c) => (
                  <label key={c.name} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      value={c.value}
                      checked={filterColors.includes(c.name)}
                      onChange={onColorChange}
                      className="h-4 w-4 border-gray-300 text-[#F93A00] focus:ring-[#F93A00] rounded-none"
                    />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900">{c.name}</span>
                  </label>
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      )}

      {/* Sizes */}
      {sizes.length > 0 && (
        <Disclosure defaultOpen>
          {({ open }) => (
            <div className="border-t border-gray-200 pt-4">
              <DisclosureButton className="flex w-full items-center justify-between text-sm font-bold uppercase tracking-wide text-gray-800">
                Sizes
                <ChevronDownIcon className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </DisclosureButton>
              <DisclosurePanel className="mt-4 flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <label key={s.name} className="cursor-pointer">
                    <input
                      type="checkbox"
                      value={s.name}
                      checked={filterSizes.includes(s.name)}
                      onChange={onSizeChange}
                      className="sr-only"
                    />
                    <span className={`inline-block border px-3 py-1 text-xs font-semibold uppercase transition-colors ${
                      filterSizes.includes(s.name)
                        ? 'border-[#F93A00] bg-[#F93A00] text-white'
                        : 'border-gray-300 text-gray-600 hover:border-[#F93A00] hover:text-[#F93A00]'
                    }`}>
                      {s.name}
                    </span>
                  </label>
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      )}

      {/* Vendors */}
      {vendors.length > 0 && (
        <Disclosure>
          {({ open }) => (
            <div className="border-t border-gray-200 pt-4">
              <DisclosureButton className="flex w-full items-center justify-between text-sm font-bold uppercase tracking-wide text-gray-800">
                Brands
                <ChevronDownIcon className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </DisclosureButton>
              <DisclosurePanel className="mt-4 space-y-2">
                {vendors.map((v) => (
                  <label key={v.id} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      value={String(v.id)}
                      checked={filterVendors.includes(String(v.id))}
                      onChange={onVendorChange}
                      className="h-4 w-4 border-gray-300 text-[#F93A00] focus:ring-[#F93A00] rounded-none"
                    />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900">{v.name}</span>
                  </label>
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <Disclosure>
          {({ open }) => (
            <div className="border-t border-gray-200 pt-4">
              <DisclosureButton className="flex w-full items-center justify-between text-sm font-bold uppercase tracking-wide text-gray-800">
                Category
                <ChevronDownIcon className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </DisclosureButton>
              <DisclosurePanel className="mt-4 space-y-2">
                {categories.map((cat) => (
                  <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      value={String(cat.id)}
                      checked={filterCats.includes(String(cat.id))}
                      onChange={onCategoryChange}
                      className="h-4 w-4 border-gray-300 text-[#F93A00] focus:ring-[#F93A00] rounded-none"
                    />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900">{cat.name}</span>
                  </label>
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      )}

      <div className="border-t border-gray-200 pt-5">
        <button
          type="button"
          onClick={applyFilters}
          className="w-full bg-[#222222] py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-[#F93A00] transition-colors"
        >
          Apply Filters
        </button>
      </div>
    </div>
  )

  return (
    <div className="bg-white min-h-screen">
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={categoryDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:site_name" content="AntonetShop" />
      </Head>

      <Header bannerMessage={bannerMessage} navigation={navigation} />

      {/* Mobile filter drawer */}
      <Dialog open={mobileFiltersOpen} onClose={setMobileFiltersOpen} className="relative z-40 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/40 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />
        <div className="fixed inset-0 z-40 flex justify-end">
          <DialogPanel
            transition
            className="relative flex w-full max-w-xs transform flex-col overflow-y-auto bg-white py-5 px-5 shadow-xl transition duration-300 ease-in-out data-closed:translate-x-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close filters</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <FilterPanel />
          </DialogPanel>
        </div>
      </Dialog>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="border-b border-gray-200 py-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tight text-[#222222]">
              {category?.name || (searchTerm ? `Search results for “${searchTerm}”` : pageTitle)}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-500">{categoryDescription}</p>
            <p className="mt-2 text-xs font-bold uppercase tracking-widest text-[#F93A00]">{searchTerm ? `${productCount} products found for “${searchTerm}”` : `${productCount} ${productCount === 1 ? 'product' : 'products'}`}</p>
          </div>
          {/* Mobile filter button */}
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-700 hover:border-[#F93A00] hover:text-[#F93A00] transition-colors lg:hidden"
          >
            <FunnelIcon className="size-4" />
            Filters
          </button>
        </div>

        <div className="pt-8 pb-24 lg:grid lg:grid-cols-4 lg:gap-x-10">
          {/* Desktop Filters sidebar */}
          <aside className="hidden lg:block lg:col-span-1">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6">Refine By</h2>
            <FilterPanel />
          </aside>

          {/* Product grid */}
          <section className="lg:col-span-3">
            {/* Toolbar: count + per-page selector */}
            {productItems.length > 0 && (
              <div className="mb-5 flex items-center justify-between">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {productCount} {productCount === 1 ? 'product' : 'products'}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="per-page" className="text-xs text-gray-500 uppercase tracking-wide">Show</label>
                  <select
                    id="per-page"
                    value={perPage}
                    onChange={(e) => changePerPage(e.target.value)}
                    className="border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:outline-none focus:border-[#F93A00]"
                  >
                    {[12, 24, 36, 48].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {productItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <p className="text-lg font-bold uppercase tracking-wide text-gray-400">No products found</p>
                <p className="mt-2 text-sm text-gray-400">{category ? `There are no products in ${category.name} yet.` : 'Try adjusting your filters or search term.'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                {productItems.map((product) => (
                  <a
                    key={product.id}
                    href={route('public.productDetail', product.slug ?? product.name)}
                    className="group block"
                  >
                    {/* Product image */}
                    <div className="relative overflow-hidden bg-white aspect-square">
                      <img
                        src={product.image_url || '/default.png'}
                        alt={product.name}
                        className="absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                      />
                      {/* New badge */}
                      <span className="absolute top-2 left-2 bg-[#3CBD45] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        New
                      </span>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                    </div>
                    {/* Product info */}
                    <div className="mt-2 px-0.5">
                      <h3 className="text-xs text-gray-800 leading-tight line-clamp-2">{product.name}</h3>
                      {product.price && (
                        <p className="mt-1 text-sm font-bold text-[#F93A00]">J${Number(product.price).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JMD</p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* Pagination */}
            {last_page > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={!prev_page_url}
                  onClick={() => goToPage(prev_page_url)}
                  className={`px-5 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    prev_page_url
                      ? 'border-gray-300 text-gray-700 hover:border-[#F93A00] hover:text-[#F93A00]'
                      : 'border-gray-200 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  &larr; Prev
                </button>

                <span className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {current_page} / {last_page}
                </span>

                <button
                  type="button"
                  disabled={!next_page_url}
                  onClick={() => goToPage(next_page_url)}
                  className={`px-5 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    next_page_url
                      ? 'border-gray-300 text-gray-700 hover:border-[#F93A00] hover:text-[#F93A00]'
                      : 'border-gray-200 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </section>
        </div>
      </div>

      <Footer footerNavigation={footerNavigation} />
    </div>
  )
}
